import React, { useState } from "react";
import { Calculator, TrendingUp, Sparkles, AlertCircle, RefreshCw } from "lucide-react";

export default function ResourcePage({ pageKey }) {
  // Gated Whitepaper State
  const [email, setEmail] = useState("");
  const [isGatedSubmitted, setIsGatedSubmitted] = useState(false);

  // ROI Calculator States
  const [annualSpend, setAnnualSpend] = useState(5000000);
  const [vendorCount, setVendorCount] = useState(50);
  const [manualAuditHours, setManualAuditHours] = useState(120);
  const [roiResult, setRoiResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const handleCalculateRoi = (e) => {
    e.preventDefault();
    setCalculating(true);
    setTimeout(() => {
      // Formulating standard strategic estimates:
      // Risk reduction: average 1.5% of spend saved from compliance disruptions
      // Audit time saved: 80% reduction in manual hours (assumed rate $65/hr)
      // Consolidated spend opportunities: ~2.5% of total spend from software/licensing duplicate overlaps
      const riskSavings = annualSpend * 0.015;
      const auditSavings = manualAuditHours * 52 * 65 * 0.8;
      const overlapSavings = annualSpend * 0.025;
      const totalSavings = riskSavings + auditSavings + overlapSavings;

      setRoiResult({
        total: Math.round(totalSavings),
        risk: Math.round(riskSavings),
        audit: Math.round(auditSavings),
        overlap: Math.round(overlapSavings)
      });
      setCalculating(false);
    }, 600);
  };

  const handleGatedSubmit = (e) => {
    e.preventDefault();
    console.log("Gated content delivery requested for:", email);
    setIsGatedSubmitted(true);
  };

  if (pageKey === "blog") {
    const blogPosts = [
      {
        title: "Mitigating Global Supply Chain Compliance Disruption in 2026",
        desc: "A detailed analysis of automated compliance tracking frameworks and immediate SLA risk alerts for Heads of Procurement.",
        date: "Jan 12, 2026",
        readTime: "8 min read"
      },
      {
        title: "The Silent Profit Killer: Maverick Spend in Enterprise SaaS",
        desc: "How decentralized departments duplicate licensing and key recommendations to identify consolidation overlaps within 30 days.",
        date: "Dec 22, 2025",
        readTime: "6 min read"
      }
    ];

    return (
      <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid="blog-resources-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="space-y-4 mb-16">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#EA7C3D]">Resources & Industry Knowledge</div>
            <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter">Aroveniq Insights Center</h1>
            <p className="text-base text-zinc-400 max-w-2xl">Read our latest columns, detailed operational guidelines, and procurement whitepapers.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.map((post, i) => (
              <div key={i} className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-[#7C3AED]/50 hover:bg-white/10 transition-all duration-300 space-y-4">
                <div className="flex justify-between items-center text-xs text-zinc-500 font-mono">
                  <span>{post.date}</span>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="text-xl font-bold font-display text-white">{post.title}</h3>
                <p className="text-sm text-[#ABA3C2] leading-relaxed font-body">{post.desc}</p>
                <div className="pt-2">
                  <span className="text-sm font-semibold text-[#EA7C3D] hover:underline cursor-pointer">Read Article &gt;</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (pageKey === "whitepapers") {
    return (
      <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid="whitepapers-resources-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="space-y-4 mb-16 text-center">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#EA7C3D]">Gated Technical Whitepaper</div>
            <h1 className="text-4xl font-black font-display tracking-tighter">The State of Vendor Intelligence 2026</h1>
            <p className="text-base text-zinc-400 max-w-xl mx-auto">Get complete diagnostic checklists, comparative spend tracking matrices, and live SLA triggers.</p>
          </div>

          <div className="p-8 rounded-3xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl max-w-xl mx-auto">
            {!isGatedSubmitted ? (
              <form onSubmit={handleGatedSubmit} className="space-y-6" data-testid="gated-download-form">
                <h3 className="text-lg font-bold text-white font-display">Download the Free PDF Report</h3>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Business Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="gated-email-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                    placeholder="sarah@company.com"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="gated-submit"
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] text-white font-semibold text-sm hover:shadow-[0_0_15px_rgba(124,58,237,0.4)] transition-all"
                >
                  Download Whitepaper
                </button>
              </form>
            ) : (
              <div className="text-center py-8 space-y-4" data-testid="gated-success">
                <div className="w-12 h-12 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mx-auto text-xl font-bold">✓</div>
                <h3 className="text-xl font-bold text-white font-display">Check your Inbox!</h3>
                <p className="text-sm text-zinc-400">We have dispatched &quot;The State of Vendor Intelligence 2026&quot; report to <strong className="text-white">{email}</strong>.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback to ROI Calculator Page
  return (
    <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid="roi-calculator-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="space-y-4 mb-16 text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide">
            <Calculator className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>Interactive Financial Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter">Aroveniq Procurement ROI Calculator</h1>
          <p className="text-base text-zinc-400 max-w-2xl mx-auto">
            Input your current annual vendor spend and manual audit overhead to estimate immediate risk & overlap savings.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Form */}
          <div className="lg:col-span-5 p-8 rounded-3xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl space-y-6">
            <form onSubmit={handleCalculateRoi} className="space-y-5" data-testid="roi-form">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Annual Vendor Spend ($)</label>
                <input
                  type="number"
                  value={annualSpend}
                  onChange={(e) => setAnnualSpend(Number(e.target.value))}
                  data-testid="roi-annual-spend-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Total Active Vendors</label>
                <input
                  type="number"
                  value={vendorCount}
                  onChange={(e) => setVendorCount(Number(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Manual Audit Hours (per Vendor / year)</label>
                <input
                  type="number"
                  value={manualAuditHours}
                  onChange={(e) => setManualAuditHours(Number(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={calculating}
                data-testid="roi-submit-button"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] text-white font-semibold text-sm hover:shadow-[0_0_15px_rgba(124,58,237,0.4)] transition-all flex items-center justify-center space-x-2"
              >
                {calculating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                <span>{calculating ? "Calculating Estimates..." : "Calculate Savings Potential"}</span>
              </button>
            </form>
          </div>

          {/* Results display */}
          <div className="lg:col-span-7 p-8 rounded-3xl bg-black/40 border border-white/5 min-h-[400px] flex flex-col justify-center">
            {roiResult ? (
              <div className="space-y-6" data-testid="roi-result-display">
                <div className="border-b border-white/10 pb-4">
                  <h3 className="text-lg font-bold text-white font-display">Aroveniq Solutions Estimated Annual Value</h3>
                  <p className="text-xs text-zinc-500">Based on standard procurement risk safeguards & consolidation strategies.</p>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-tr from-[#7C3AED]/20 to-[#EA7C3D]/20 border border-white/10 text-center">
                  <div className="text-xs text-zinc-300 uppercase tracking-widest font-semibold">Total Estimated Annual Savings</div>
                  <div className="text-4xl lg:text-5xl font-black text-white font-mono mt-2">${roiResult.total.toLocaleString()}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  <div className="p-4 rounded-xl bg-white/5 text-center">
                    <div className="text-[10px] text-zinc-500">Disruption Risk Avoidance</div>
                    <div className="text-lg font-bold text-white font-mono mt-1">${roiResult.risk.toLocaleString()}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 text-center">
                    <div className="text-[10px] text-zinc-500">Manual Audit Reductions</div>
                    <div className="text-lg font-bold text-white font-mono mt-1">${roiResult.audit.toLocaleString()}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 text-center">
                    <div className="text-[10px] text-zinc-500">License/Overlap Savings</div>
                    <div className="text-lg font-bold text-white font-mono mt-1">${roiResult.overlap.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-3 text-zinc-500 py-12">
                <AlertCircle className="w-12 h-12 mx-auto text-[#6E6785]" />
                <h3 className="text-lg font-bold text-white font-display">Savings Estimates Pending</h3>
                <p className="text-xs max-w-sm mx-auto">
                  Adjust active vendor metrics on the left and select calculate to view risk mitigations and operational savings.
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}